import contoursLeft from '@/assets/contours_left.svg';
import contoursRight from '@/assets/contours_right.svg';
import pqLogoWhite from '@/assets/pq_logo.svg';
import { Box, Button, Divider, Image, Stack, Text, Title } from '@mantine/core';
import { useAuth } from '@workos-inc/authkit-react';
import { useRef, useState } from 'react';

import AppInstallPrompt from '@/components/AppInstallPrompt';
import type { PwaInstallControls } from '@/lib/pwa-install';

interface LoginPageProps {
    install?: PwaInstallControls;
}

const LoginPage: React.FC<LoginPageProps> = ({ install }) => {
    const { signIn } = useAuth();
    const signingInRef = useRef(false);
    const [signingIn, setSigningIn] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSignIn = async () => {
        if (signingInRef.current) return;
        signingInRef.current = true;
        setSigningIn(true);
        setError(null);
        try {
            await signIn();
        } catch {
            setError('Unable to open sign in. Check your connection and try again.');
        } finally {
            signingInRef.current = false;
            setSigningIn(false);
        }
    };

    return (
        <Box component="main" h="100%" bg="pq-red" pos="relative" style={{ overflow: 'hidden' }}>
            <Image
                h="50dvh"
                fit="contain"
                style={{ position: 'absolute', left: '-50vw', top: '-5dvh', pointerEvents: 'none', filter: 'brightness(0) invert(1)' }}
                src={contoursLeft}
                alt=""
                aria-hidden="true"
            />
            <Image
                w="50dvw"
                fit="contain"
                style={{ position: 'absolute', right: 0, bottom: 0, pointerEvents: 'none', filter: 'brightness(0) invert(1)' }}
                src={contoursRight}
                alt=""
                aria-hidden="true"
            />
            <Box h="100%" pos="relative" style={{ overflowY: 'auto', touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
                <Stack align="stretch" justify="center" gap="xl" mih="100%" maw={440} mx="auto" px="xl" py={32}>
                    <Image w="100%" h={100} fit="contain" src={pqLogoWhite} alt="ParcelQuest" />
                    <Text ta="center" c="white">Sign in to access the most comprehensive CA property data on planet Earth.</Text>
                    <Stack gap="sm">
                        <Button
                            onClick={handleSignIn}
                            loading={signingIn}
                            size="lg"
                            radius="xl"
                            fullWidth
                            variant="filled"
                            color="white"
                            c="pq-red"
                        >
                            Sign In
                        </Button>
                        {error && <Text c="white" size="sm" ta="center" role="alert">{error}</Text>}
                    </Stack>
                    {install && <AppInstallPrompt install={install} />}
                    <Divider color="white" />
                    <Stack gap={4} ta="center" c="white">
                        <Title order={1} size="h2">ParcelQuest Mobile</Title>
                        {import.meta.env.VITE_APP_VERSION && (
                            <Text size="sm">Version {import.meta.env.VITE_APP_VERSION}</Text>
                        )}
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default LoginPage;
